// The shared parameter state: generative/ writes it, visuals/ and app/ read it.
// ONE live object, mutated in place each tick. Consumers MUST read per-frame and
// never cache the object identity expecting immutability.

export type AxisName = 'density' | 'register' | 'brightness' | 'harmonicity' | 'space';

export const AXES: readonly AxisName[] = [
  'density',
  'register',
  'brightness',
  'harmonicity',
  'space',
] as const;

export type AxisVector = Record<AxisName, number>; // all in [0,1]

export type Mood = 'abyssal' | 'tidepool' | 'storm';

export const MOODS: readonly Mood[] = ['abyssal', 'tidepool', 'storm'] as const;

export interface PitchFieldSnapshot {
  rootHz: number;
  ratios: number[];
  /** 0..1, how stretched/wide the scale currently is. */
  widening: number;
}

export interface ParameterState {
  /** Smoothed normalized axes in [0,1]; visuals/app read state.norm.*. */
  norm: AxisVector;
  /** Pre-mapped engineering units so consumers don't re-derive curves. */
  units: {
    densityOnsetsPerMin: number; // log 2..40
    registerCentroidHz: number; // log 110..880
    brightnessTilt: number; // lin -1..+1
    harmonicityB: number; // log 0.0001..0.02
    spaceReverb: number; // lin 0..1
  };
  mood: { from: Mood; to: Mood; blend: number };
  /** 0..1 slow tension/release. */
  tension: number;
  pitchField: PitchFieldSnapshot;
  /** x1/x10-scaled engine clock, seconds. */
  engineTimeSec: number;
}

/** Construct a fresh, neutral parameter state (used at boot and in tests). */
export function createParameterState(): ParameterState {
  return {
    norm: { density: 0.3, register: 0.4, brightness: 0.4, harmonicity: 0.4, space: 0.6 },
    units: {
      densityOnsetsPerMin: 6,
      registerCentroidHz: 220,
      brightnessTilt: -0.2,
      harmonicityB: 0.0008,
      spaceReverb: 0.6,
    },
    mood: { from: 'abyssal', to: 'abyssal', blend: 0 },
    tension: 0.2,
    pitchField: { rootHz: 110, ratios: [1, 9 / 8, 6 / 5, 4 / 3, 3 / 2, 8 / 5, 9 / 5, 2], widening: 0 },
    engineTimeSec: 0,
  };
}
